import React, { useState, useEffect } from 'react';
import { fetchInterviewFeedback, downloadResume } from '../services/apiService';

const InterviewFeedbackModal = ({ 
  isOpen, 
  onClose, 
  interview, 
  onSubmitFeedback 
}) => {
  const [skills, setSkills] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloadingResume, setIsDownloadingResume] = useState(false);

  // Check if this is a completed interview (read-only mode)
  const isCompleted = interview?.status === 'Selected' || 
                     interview?.status === 'Rejected' || 
                     interview?.status === 'NoShow' ||
                     interview?.feedback;

  // Helper function to merge budget skills with candidate skills
  const mergeSkillsData = (budgetCitySkills, candidateSkills) => {
    if (!budgetCitySkills || !Array.isArray(budgetCitySkills)) return [];
    
    return budgetCitySkills.map(budgetSkill => {
      const candidateSkill = candidateSkills?.find(
        skill => skill.skillId === budgetSkill.skillId
      );
      
      return {
        skillId: budgetSkill.skillId,
        skillName: budgetSkill.skillName,
        rating: candidateSkill?.rating || 0
      };
    });
  };

  // Fetch interview feedback data when modal opens
  useEffect(() => {
    const loadInterviewFeedback = async () => {
      if (isOpen && interview && interview.interviewId) {
        setLoading(true);
        setError(null);
        
        try {
          const response = await fetchInterviewFeedback(interview.interviewId);
          
          if (response.success) {
            // Process skills data immediately
            const initialSkills = mergeSkillsData(
              response.data.budgetCitySkills,
              response.data.candidateSkills
            );
            setSkills(initialSkills);
            setComment(response.data?.interviewFeedback || interview.feedback || '');
          } else {
            setError(response.error || 'Failed to load interview feedback data');
            setSkills([]);
            setComment('');
          }
        } catch (err) {
          setError('Failed to load interview feedback data');
          console.error('Error loading interview feedback:', err);
          setSkills([]);
          setComment('');
        } finally {
          setLoading(false);
        }
      }
    };

    loadInterviewFeedback();
  }, [isOpen, interview?.interviewId, interview, isCompleted]);

  // Handle body scroll and cleanup when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSkillRating = (skillIndex, rating) => {
    if (isCompleted) return; // Don't allow changes for completed interviews
    
    setSkills(prev => 
      prev.map((skill, index) => 
        index === skillIndex ? { ...skill, rating } : skill
      )
    );
  };

  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleResumeDownload = async () => {
    if (isDownloadingResume) return;

    const candidateId = interview?.candidateId;
    const resumeFilePath = interview?.resumeFilePath;

    if (!candidateId || !resumeFilePath) {
      showNotification('Resume not available for this candidate');
      return;
    }

    try {
      setIsDownloadingResume(true);
      const result = await downloadResume(candidateId, resumeFilePath);
      if (!result.success) {
        showNotification(result.error || 'Failed to download resume');
      } else {
        showNotification('Resume download started', 'success');
      }
    } catch (err) {
      console.error('Error downloading resume:', err);
      showNotification('Failed to download resume');
    } finally {
      setIsDownloadingResume(false);
    }
  };

  const validateForm = (action) => {
    // For NoShow, no validation required (feedback is optional)
    if (action === 'NoShow') {
      return true;
    }
    
    // For Approve and Reject, skills rating is mandatory
    const unratedSkills = skills.filter(skill => skill.rating === 0);
    if (unratedSkills.length > 0) {
      showNotification('Skill Mandatory');
      return false;
    }
    
    if (!comment.trim()) {
      showNotification('Please provide feedback comments');
      return false;
    }
    
    return true;
  };

  const handleActionSubmit = async (action) => {
    if (!validateForm(action) || isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Convert skills to the format expected by the API
      let skillsRatings;
      
      if (!skills || skills.length === 0) {
        // If budget skills is empty, send {"0": 0}
        skillsRatings = {"0": 0};
      } else {
        // If budget skills is not empty, key = skillId, value = rating
        skillsRatings = {};
        skills.forEach(skill => {
          skillsRatings[skill.skillId] = skill.rating;
        });
      }
      
      const feedbackData = {
        candidateStatus: action,
        interviewFeedback: comment,
        skillsRatings: skillsRatings
      };
      
      const result = await onSubmitFeedback(feedbackData);
      
      // Show success message from API response
      if (result && result.message) {
        showNotification(result.message, 'success');
        // Close modal after showing success message
        setTimeout(() => onClose(), 1500);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showNotification('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStarRating = (skill) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <button
          key={i}
          type="button"
          onClick={() => handleSkillRating(skills.indexOf(skill), i)}
          disabled={isCompleted}
          style={{
            background: 'none',
            border: 'none',
            cursor: isCompleted ? 'default' : 'pointer',
            padding: '2px',
            fontSize: '20px',
            color: i <= skill.rating ? '#fbbf24' : '#d1d5db',
            transition: 'color 0.2s ease',
            opacity: isCompleted ? 0.7 : 1
          }}
          onMouseEnter={(e) => {
            if (!isCompleted && i > skill.rating) {
              e.target.style.color = '#fde68a';
            }
          }}
          onMouseLeave={(e) => {
            if (!isCompleted) {
              e.target.style.color = i <= skill.rating ? '#fbbf24' : '#d1d5db';
            }
          }}
        >
          ★
        </button>
      );
    }
    return stars;
  };

  const renderNotification = () => {
    if (!notification) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          backgroundColor: notification.type === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 10000,
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '320px',
          textAlign: 'left'
        }}
      >
        {notification.message}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {renderNotification()}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '10px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: window.innerWidth <= 768 ? '8px' : '12px',
          maxWidth: window.innerWidth <= 768 ? '95vw' : '800px',
          width: '100%',
          maxHeight: window.innerWidth <= 768 ? '90vh' : '95vh',
          overflow: 'hidden',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
            padding: window.innerWidth <= 768 ? '16px' : '24px',
            color: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: window.innerWidth <= 480 ? 'flex-start' : 'center',
              // flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
              gap: window.innerWidth <= 480 ? '12px' : '0'
            }}>
              <div>
                <h2 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: window.innerWidth <= 768 ? '20px' : '24px', 
                  fontWeight: '700',
                  lineHeight: '1.2'
                }}>
                  Interview Feedback
                </h2>
                <p style={{ 
                  margin: '0', 
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px', 
                  opacity: '0.9',
                  lineHeight: '1.4'
                }}>
                  {interview?.designation}
                </p>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '50%',
                  // width: '40px',
                  // height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'end',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '20px',
                  transition: 'background-color 0.2s ease'
                }}
                // onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                // onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          {/* Content */}
          <div style={{
            padding: window.innerWidth <= 768 ? '16px' : '24px',
            maxHeight: window.innerWidth <= 768 ? 'calc(90vh - 120px)' : 'calc(90vh - 140px)',
            overflowY: 'auto'
          }}>
            {/* Candidate Information */}
            <div style={{
              backgroundColor: '#f8fafc',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '24px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{
                margin: '0 0 16px 0',
                fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                Candidate Details
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
                gridTemplateRows: window.innerWidth <= 768 ? 'auto' : 'repeat(2, auto)',
                gap: window.innerWidth <= 768 ? '16px' : '20px 24px',
                alignItems: 'start'
              }}>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>
                    Candidate Name
                  </p>
                  <p style={{ margin: '0', fontSize: '14px', fontWeight: '500', color: '#334155' }}>
                    <i className="fas fa-user" style={{ marginRight: '6px', color: '#2563eb' }}></i>
                    {interview?.candidateName}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>
                    Interview Date
                  </p>
                  <p style={{ margin: '0', fontSize: '14px', fontWeight: '500', color: '#334155' }}>
                    <i className="fas fa-calendar" style={{ marginRight: '6px', color: '#2563eb' }}></i>
                    {(() => {
                      const dateTimeStr = interview?.interviewDateTime;
                      const isInvalidDate = !dateTimeStr || 
                                           dateTimeStr.trim() === '' || 
                                           dateTimeStr.startsWith('0000-00-00');
                      
                      if (isInvalidDate && (interview?.status === 'Approved' || interview?.status === 'Pending')) {
                        return 'Not scheduled';
                      }
                      
                      if (!dateTimeStr) return 'N/A';
                      
                      try {
                        const [datePart, timePart] = dateTimeStr.split(' ');
                        const date = new Date(datePart + 'T' + timePart);
                        
                        if (isNaN(date.getTime())) {
                          return 'N/A';
                        }
                        
                        return date.toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        });
                      } catch (e) {
                        return 'N/A';
                      }
                    })()}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>
                    Interview Time
                  </p>
                  <p style={{ margin: '0', fontSize: '14px', fontWeight: '500', color: '#334155' }}>
                    <i className="fas fa-clock" style={{ marginRight: '6px', color: '#2563eb' }}></i>
                    {(() => {
                      const dateTimeStr = interview?.interviewDateTime;
                      const isInvalidDate = !dateTimeStr || 
                                           dateTimeStr.trim() === '' || 
                                           dateTimeStr.startsWith('0000-00-00');
                      
                      if (isInvalidDate && (interview?.status === 'Approved' || interview?.status === 'Pending')) {
                        return 'Not scheduled';
                      }
                      
                      if (!dateTimeStr) return 'N/A';
                      
                      try {
                        const [datePart, timePart] = dateTimeStr.split(' ');
                        const date = new Date(datePart + 'T' + timePart);
                        
                        if (isNaN(date.getTime())) {
                          return 'N/A';
                        }
                        
                        return date.toLocaleTimeString('en-IN', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: true 
                        });
                      } catch (e) {
                        return 'N/A';
                      }
                    })()}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>
                    Mobile
                  </p>
                  <p style={{ margin: '0', fontSize: '14px', fontWeight: '500', color: '#334155' }}>
                    <i className="fas fa-phone" style={{ marginRight: '6px', color: '#2563eb' }}></i>
                    {interview?.candidateMobile || 'N/A'}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>
                    Resume
                  </p>
                  {interview?.resumeFilePath ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={handleResumeDownload}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleResumeDownload();
                          }
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          color: isDownloadingResume ? '#9ca3af' : '#2563eb',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: isDownloadingResume ? 'not-allowed' : 'pointer',
                          textDecoration: 'none',
                          transition: 'color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!isDownloadingResume) {
                            e.currentTarget.style.color = '#1d4ed8';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = isDownloadingResume ? '#9ca3af' : '#2563eb';
                        }}
                      >
                        <i className="fas fa-download" style={{ fontSize: '14px' }}></i>
                        {isDownloadingResume ? 'Downloading…' : window.innerWidth < 450 ? 'Download' : 'Download Resume'}
                      </span>
                      <span style={{ fontSize: '12px', color: '#64748b', wordBreak: 'break-word', display: 'none'}}>
                        {interview.resumeFilePath}
                      </span>
                    </div>
                  ) : (
                    <p style={{ margin: '0', fontSize: '13px', color: '#9ca3af' }}>Not uploaded</p>
                  )}
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ 
                  display: 'inline-block',
                  width: '20px',
                  height: '20px',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #2563eb',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ marginTop: '10px', color: '#6b7280' }}>Loading interview data...</p>
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div style={{ 
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <p style={{ color: '#dc2626', margin: 0 }}>{error}</p>
              </div>
            )}

            {/* Skills Rating */}
            {!loading && !error && (
              <div style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '1px solid #e2e8f0'
              }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1e293b'
                }}>
                  <i className="fas fa-star" style={{ marginRight: '8px', color: '#2563eb' }}></i>
                  Rate this candidate
                </h3>
                
                {skills.map((skill, index) => (
                  <div key={skill.id} style={{
                    marginBottom: index === skills.length - 1 ? '0px' : '20px',
                    paddingBottom: index === skills.length - 1 ? '0px' : '16px',
                    borderBottom: skills.indexOf(skill) === skills.length - 1 ? 'none' : '1px solid #f1f5f9'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: window.innerWidth < 450 ? 'column' : 'row',
                      justifyContent: window.innerWidth < 450 ? 'center' : 'space-between',
                      alignItems: window.innerWidth < 450 ? 'start' : 'center',
                      // marginBottom: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <span style={{
                          fontSize: '16px',
                          fontWeight: '500',
                          color: '#334155'
                        }}>
                          {skill.skillName}
                          {skill.required && (
                            <span style={{ color: '#dc2626', marginLeft: '4px' }}>*</span>
                          )}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlignLast: window.innerWidth < 450 ? 'justify' : 'auto'}}>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          {renderStarRating(skill)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Feedback Comment */}
            {!loading && !error && (
              <div style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '1px solid #e2e8f0'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1e293b'
                }}>
                  <i className="fas fa-comment" style={{ marginRight: '8px', color: '#2563eb' }}></i>
                  Feedback Comment *
                </h3>
                <textarea
                  value={comment}
                  onChange={(e) => !isCompleted && setComment(e.target.value)}
                  placeholder={isCompleted ? "Feedback provided during interview" : "Please provide detailed feedback about the candidate's performance..."}
                  readOnly={isCompleted}
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    backgroundColor: isCompleted ? '#f9fafb' : 'white',
                    color: isCompleted ? '#6b7280' : '#1f2937',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => !isCompleted && (e.target.style.borderColor = '#3b82f6')}
                  onBlur={(e) => !isCompleted && (e.target.style.borderColor = '#e5e7eb')}
                />
              </div>
            )}

            {/* Action Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: window.innerWidth <= 768 ? 'center' :'flex-end',
                gap: '12px',
                marginTop: '24px',
                marginBottom: window.innerWidth <= 768 ? '8px' : '0',
                flexWrap: 'wrap'
              }}>
                {isCompleted ? (
                  // Close button for completed interviews
                  <button
                    onClick={onClose}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#d1d5db',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = '#9ca3af')}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = '#d1d5db')}
                  >
                    Close
                  </button>
                ) : (
                  // Action buttons for pending interviews
                  !loading && !error && (
                    <>
                      <button
                        onClick={() => handleActionSubmit('NoShow')}
                        disabled={isSubmitting}
                        style={{
                          padding: window.innerWidth <= 768 ? '10px 12px' : '12px 16px',
                          backgroundColor: isSubmitting ? '#9ca3af' : '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                          fontWeight: '500',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'background-color 0.2s ease',
                          minWidth: '90px',
                          height: '40px'
                        }}
                      >
                        <i className="fas fa-user-times"></i>
                        {isSubmitting ? 'Processing...' : 'No Show'}
                      </button>
                        
                      <button
                        onClick={() => handleActionSubmit('Rejected')}
                        disabled={isSubmitting}
                        style={{
                          padding: window.innerWidth <= 768 ? '10px 12px' : '12px 16px',
                          backgroundColor: isSubmitting ? '#9ca3af' : '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                          fontWeight: '500',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'background-color 0.2s ease',
                          minWidth: '90px',
                          height: '40px'
                        }}
                      >
                        <i className="fas fa-times-circle"></i>
                        {isSubmitting ? 'Processing...' : 'Reject'}
                      </button>
                        
                      <button
                        onClick={() => handleActionSubmit('Selected')}
                        disabled={isSubmitting}
                        style={{
                          padding: window.innerWidth <= 768 ? '10px 12px' : '12px 16px',
                          backgroundColor: isSubmitting ? '#9ca3af' : '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                          fontWeight: '500',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'background-color 0.2s ease',
                          minWidth: '90px',
                          height: '40px'
                        }}
                      >
                        <i className="fas fa-check-circle"></i>
                        {isSubmitting ? 'Processing...' : 'Approve'}
                      </button>
                    </>
                  )
                )}
              </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InterviewFeedbackModal;
